/**
 * hooks/useDarkMode.ts
 * Dark mode — reads system preference, persists manual toggle to localStorage.
 * Usage: const { isDark, toggle } = useDarkMode()
 * Adds/removes 'dark' class on <html> — all dark: Tailwind variants then work.
 */

import { useState, useEffect, useCallback } from 'react';

const KEY = 'Jötr_theme';

export function useDarkMode() {
  const getInitial = (): boolean => {
    const stored = localStorage.getItem(KEY);
    if (stored !== null) return stored === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const [isDark, setIsDark] = useState<boolean>(() => {
    try { return getInitial(); } catch { return false; }
  });

  // Apply class to <html> whenever isDark changes
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) { root.classList.add('dark'); root.setAttribute('data-theme','dark'); }
    else         { root.classList.remove('dark'); root.setAttribute('data-theme','light'); }
    localStorage.setItem(KEY, isDark ? 'dark' : 'light');
  }, [isDark]);

  // Listen for system preference changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem(KEY)) setIsDark(e.matches);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const toggle = useCallback(() => setIsDark(d => !d), []);

  return { isDark, toggle };
}
